import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from './components/Sidebar';
import AdminBottomNav from './components/AdminBottomNav';
import AdminMobileHeader from './components/AdminMobileHeader';
import PushPrompt from '../../components/PushPrompt';
import { useTenant } from '../../contexts/TenantContext';
import { useFeature } from '../../hooks/useFeature';

/**
 * Generic admin dashboard.
 *
 * Per R8 — this serves the Generic tenant. TCN gets a separate component
 * later. For now, when a TCN agency_owner lands here we render the same
 * generic view (until the TCN-specific dashboard is built).
 *
 * R2 — no decrypted financial data shown here. We display counts
 * (creators, agents) and operational signals (server breakdown, recent
 * creators). Diamonds/payout/commission would require Edge Function
 * decryption and are out of scope for the dashboard surface anyway.
 *
 * R9 — Violations / recruitment / sessions / referral / quit_monitor are
 * Phase-2 (scraping-dependent). Their tables exist but no UI surface here.
 */

const StatCard = ({ label, value, sublabel, accent = 'primary', loading }) => (
  <div className="rounded-2xl bg-card border border-border p-4 md:p-5 shadow-elevation-1">
    <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
      {label}
    </div>
    {loading ? (
      <div className="h-8 w-20 rounded skeleton mt-3" />
    ) : (
      <div className="text-2xl md:text-3xl font-heading font-bold text-foreground mt-2 tabular-nums">
        {value}
      </div>
    )}
    {sublabel && !loading && (
      <div className={`text-xs mt-1 text-${accent === 'primary' ? 'primary' : 'muted-foreground'}`}>
        {sublabel}
      </div>
    )}
  </div>
);

const SectionHeader = ({ title, action }) => (
  <div className="flex items-center justify-between mb-3">
    <h2 className="font-heading text-base md:text-lg font-semibold text-foreground">{title}</h2>
    {action}
  </div>
);

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { tenantSlug, tenantName, agencyName, agencyId, tenantId, servers, loading: tenantLoading } = useTenant();
  const hasCashback = useFeature('cashback');
  const hasMorningBriefing = useFeature('morning_briefing');

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const [stats, setStats] = useState({ creators: 0, agents: 0 });
  const [statsLoading, setStatsLoading] = useState(true);

  const [serverBreakdown, setServerBreakdown] = useState([]);
  const [recentCreators, setRecentCreators] = useState([]);

  // Email for sidebar / mobile header
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.email) setUserEmail(session.user.email);
    });
  }, []);

  // Fetch all dashboard data once tenant context is ready
  const fetchData = useCallback(async () => {
    if (!agencyId || !tenantId) return;
    setStatsLoading(true);
    try {
      const [
        creatorsCount,
        agentsCount,
        creatorsForServers,
        creatorsList,
      ] = await Promise.all([
        supabase.from('creators').select('id', { count: 'exact', head: true }),
        supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('role', 'agent'),
        supabase.from('creators').select('server, status'),
        supabase
          .from('creators')
          .select('id, tiktok_username, display_name, server, tier, status, last_live_date, valid_days')
          .order('last_live_date', { ascending: false, nullsFirst: false })
          .limit(8),
      ]);

      setStats({
        creators: creatorsCount.count || 0,
        agents:   agentsCount.count   || 0,
      });

      // Build per-server breakdown using the agency's configured servers
      const counts = (creatorsForServers.data || []).reduce((acc, c) => {
        if (!c.server) return acc;
        if (!acc[c.server]) acc[c.server] = { total: 0, active: 0 };
        acc[c.server].total++;
        if (c.status === 'active') acc[c.server].active++;
        return acc;
      }, {});
      const breakdown = (servers || []).map((srv) => ({
        server: srv,
        total: counts[srv]?.total || 0,
        active: counts[srv]?.active || 0,
      }));
      setServerBreakdown(breakdown);

      setRecentCreators(creatorsList.data || []);
    } finally {
      setStatsLoading(false);
    }
  }, [agencyId, tenantId, servers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((v) => !v)}
        userEmail={userEmail}
      />

      <main className="flex-1 min-w-0 flex flex-col">
        <AdminMobileHeader title={t('admin.dashboard.title', 'Dashboard')} />

        {/* Desktop heading */}
        <div className="hidden md:block px-8 py-6 border-b border-border bg-card">
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                {tenantName || ''}{agencyName ? ` · ${agencyName}` : ''}
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground">
                {t('admin.dashboard.title', 'Dashboard')}
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                {t('admin.dashboard.subtitle', 'Overview of your agency operations')}
              </p>
            </div>
            {hasMorningBriefing && (
              <Link
                to="/admin/briefing"
                className="hidden lg:inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-accent/10 border border-accent/30 text-accent text-sm font-medium hover:bg-accent/15 transition-colors"
              >
                ☀ {t('admin.dashboard.morningBriefing', 'Morning briefing')}
              </Link>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-4 md:px-8 py-5 md:py-6 pb-24 md:pb-8 space-y-6">
          {/* KPIs */}
          <section>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              <StatCard
                label={t('admin.dashboard.kpi.creators', 'Creators')}
                value={stats.creators}
                loading={statsLoading || tenantLoading}
              />
              <StatCard
                label={t('admin.dashboard.kpi.agents', 'Agents')}
                value={stats.agents}
                loading={statsLoading || tenantLoading}
              />
            </div>
          </section>

          {/* Server breakdown */}
          {serverBreakdown.length > 0 && (
            <section>
              <SectionHeader
                title={t('admin.dashboard.servers.title', 'By server')}
                action={
                  <span className="text-xs text-muted-foreground">
                    {t('admin.dashboard.servers.subtitle', '{{count}} server(s) configured', {
                      count: serverBreakdown.length,
                    })}
                  </span>
                }
              />
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {serverBreakdown.map((srv) => (
                  <div key={srv.server} className="rounded-xl bg-card border border-border p-4">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                      {srv.server}
                    </div>
                    <div className="font-heading text-2xl font-bold text-foreground mt-2 tabular-nums">
                      {srv.total}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {srv.active} {t('admin.dashboard.servers.active', 'active')}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Recent creator activity */}
          <section>
            <SectionHeader
              title={t('admin.dashboard.recentCreators.title', 'Most recent activity')}
              action={
                <Link to="/admin/creators" className="text-sm text-primary hover:underline">
                  {t('admin.dashboard.viewAll', 'View all')} →
                </Link>
              }
            />
            <div className="rounded-2xl bg-card border border-border overflow-hidden">
              {statsLoading ? (
                <div className="p-4 space-y-2">
                  {[0, 1, 2].map((i) => <div key={i} className="h-12 rounded skeleton" />)}
                </div>
              ) : recentCreators.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  {t('admin.dashboard.recentCreators.empty', 'No creators yet. Invite some via the creators page.')}
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {recentCreators.map((c) => (
                    <li key={c.id} className="px-4 py-3 flex items-center justify-between gap-3">
                      <Link
                        to={`/admin/creators/${c.tiktok_username || c.id}`}
                        className="min-w-0 flex-1 hover:text-primary transition-colors"
                      >
                        <div className="font-medium text-foreground text-sm truncate">
                          {c.display_name || `@${c.tiktok_username}`}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {c.server || '—'} · {c.tier || 'untiered'} · {c.valid_days || 0} valid days
                        </div>
                      </Link>
                      <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                        {c.last_live_date
                          ? new Date(c.last_live_date).toLocaleDateString()
                          : t('admin.dashboard.neverLive', 'never')}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          {/* Phase 2 hint when no data + cashback hint when feature active */}
          {!statsLoading && stats.creators === 0 && (
            <section className="rounded-2xl bg-card border border-dashed border-border p-6 text-center">
              <div className="font-heading text-lg font-semibold text-foreground mb-1">
                {t('admin.dashboard.empty.title', 'Ready to onboard your first creators?')}
              </div>
              <p className="text-muted-foreground text-sm mb-4">
                {t('admin.dashboard.empty.body', 'Create agents, define your servers, and start adding creators. Your dashboard will populate as data flows in.')}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Link to="/admin/users" className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">
                  {t('admin.dashboard.empty.createUser', 'Create users')}
                </Link>
                <Link to="/admin/configuration" className="px-3 py-2 rounded-lg bg-card border border-border text-foreground text-sm font-medium hover:bg-muted transition-colors">
                  {t('admin.dashboard.empty.configure', 'Configure servers')}
                </Link>
              </div>
            </section>
          )}

          {hasCashback && (
            <p className="text-xs text-muted-foreground text-center">
              {t('admin.dashboard.cashbackEnabled', 'Cashback program is active for {{tenant}}.', { tenant: tenantName })}
            </p>
          )}
        </div>

        <AdminBottomNav />
      </main>

      <PushPrompt />
    </div>
  );
};

export default AdminDashboard;
